import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import "./SlotAvailability.css";

const DAYS = [
  { id: "mon", label: "M", name: "Monday" },
  { id: "tue", label: "T", name: "Tuesday" },
  { id: "wed", label: "W", name: "Wednesday" },
  { id: "thu", label: "T", name: "Thursday" },
  { id: "fri", label: "F", name: "Friday" },
  { id: "sat", label: "S", name: "Saturday" },
  { id: "sun", label: "S", name: "Sunday" },
];

const DEFAULT_TIME_SLOTS = Array.from({ length: 16 }, (_, index) => {
  const hour = 6 + index;
  const key = `${String(hour).padStart(2, "0")}00`;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? "AM" : "PM";
  return { key, label: `${hour12} ${ampm}` };
});

const FILTER_CONFIG = [
  { key: "course", label: "Course", options: ["All", "AI", "Data Science", "Web", "Cloud"] },
  { key: "mode", label: "Mode", options: ["All", "Online", "Offline", "Hybrid"] },
  { key: "shift", label: "Shift", options: ["All", "Morning", "Afternoon", "Evening"] },
  { key: "trainer", label: "Trainer", options: ["All", "Any", "Rahul", "Priya", "Vikas"] },
  { key: "spaceStatus", label: "Training Space Status", options: ["All", "Active", "Inactive"] },
  { key: "status", label: "Status", options: ["All", "Free", "Partial", "Occupied"] },
];

const STATUS_COLORS = {
  free: "#2E7D32",
  partial: "#C62828",
  occupied: "#C62828",
};

const DAY_INDEX_TO_ID = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const toStatus = (bookedCount, spacesCount) => {
  if (bookedCount === 0) return "free";
  if (bookedCount === spacesCount) return "occupied";
  return "partial";
};

const getDayIdFromDate = (dateValue) => {
  const parsed = new Date(`${dateValue}T00:00:00`);
  return DAY_INDEX_TO_ID[parsed.getDay()];
};

const normalizeSlotId = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length >= 4) return digits.slice(0, 4);
  if (digits.length === 3) return `0${digits}`;
  return digits.padStart(4, "0");
};

const formatHourLabel = (slot) => {
  const source = slot?.start || slot?.id || "";
  const normalized = normalizeSlotId(source);
  const hour24 = Number(normalized.slice(0, 2));
  if (!Number.isFinite(hour24)) return slot?.label || "";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const ampm = hour24 < 12 ? "AM" : "PM";
  return `${hour12} ${ampm}`;
};

const formatTimeAmPm = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  const fourDigitMatch = raw.match(/^(\d{1,2})(\d{2})$/);
  let hour = null;
  let minute = null;
  if (hhmmMatch) {
    hour = Number(hhmmMatch[1]);
    minute = Number(hhmmMatch[2]);
  } else if (fourDigitMatch) {
    hour = Number(fourDigitMatch[1]);
    minute = Number(fourDigitMatch[2]);
  }
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return raw;
  const ampm = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const formatMinutesToTime = (totalMinutes) => {
  const minutes = Math.max(0, Math.min(23 * 60 + 59, Number(totalMinutes) || 0));
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const getHalfHourLabels = (hourKey) => {
  const hour24 = Number(String(hourKey || "").slice(0, 2));
  if (!Number.isFinite(hour24)) return { top: "-", bottom: "-" };
  const start = hour24 * 60;
  const mid = start + 30;
  const end = start + 60;
  return {
    top: `${formatMinutesToTime(start)} - ${formatMinutesToTime(mid)}`,
    bottom: `${formatMinutesToTime(mid)} - ${formatMinutesToTime(end)}`,
  };
};

const timeToMinutes = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = Number(ampmMatch[2]);
    const ampm = ampmMatch[3].toUpperCase();
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }
  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  const hhmmssMatch = raw.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (hhmmMatch) {
    return Number(hhmmMatch[1]) * 60 + Number(hhmmMatch[2]);
  }
  if (hhmmssMatch) {
    return Number(hhmmssMatch[1]) * 60 + Number(hhmmssMatch[2]);
  }
  const fourDigitMatch = raw.match(/^(\d{1,2})(\d{2})$/);
  if (fourDigitMatch) {
    return Number(fourDigitMatch[1]) * 60 + Number(fourDigitMatch[2]);
  }
  return null;
};

const hasOverlap = (startA, endA, startB, endB) => {
  if (
    !Number.isFinite(startA) ||
    !Number.isFinite(endA) ||
    !Number.isFinite(startB) ||
    !Number.isFinite(endB)
  ) {
    return false;
  }
  return startA < endB && endA > startB;
};

const getBatchCodeLabel = (booking) => {
  const directCode =
    booking?.batchCode ||
    booking?.batch_code ||
    booking?.code ||
    "";
  if (String(directCode).trim()) return String(directCode).trim();

  const display = String(booking?.batchDisplay || "").trim();
  if (display) {
    const [firstToken = ""] = display.split(/\s+/);
    if (firstToken) return firstToken;
  }

  const fallback = String(booking?.batchName || booking?.batchId || "-").trim();
  return fallback || "-";
};

const SlotAvailability = () => {
  const { userData } = useAuthStore();
  const token = localStorage.getItem("token");
  const organizationId =
    userData?.organization?.organization_id ||
    userData?.activeOrganizationEntity?.organization_id ||
    userData?.organization_id;
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [filters, setFilters] = useState({
    course: "All",
    mode: "All",
    shift: "All",
    trainer: "All",
    spaceStatus: "Active",
    status: "All",
  });
  const [spaces, setSpaces] = useState([]);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [rowStatus, setRowStatus] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [hoverTimeState, setHoverTimeState] = useState(null);
  const rootRef = useRef(null);
  const [isCompactView, setIsCompactView] = useState(false);

  const sendPrompt = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const selectedDayId = getDayIdFromDate(selectedDate);
      const payload = {
        organization_id: organizationId,
        date: selectedDate,
        dayId: selectedDayId,
        activeDays: [selectedDayId],
        ...filters,
      };
      const response = await axios.post(`${MAIN_URL}/api/ems/batches/schedule-board`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = response?.data || {};
      setSpaces(Array.isArray(data.spaces) ? data.spaces : []);
      setSlots(Array.isArray(data.slots) ? data.slots : []);
      setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      setRowStatus(Array.isArray(data.rowStatus) ? data.rowStatus : []);
      setFilterOptions(data.filterOptions || {});
    } catch (error) {
      console.error("Failed to fetch schedule board", error);
      setSpaces([]);
      setSlots([]);
      setBookings([]);
      setRowStatus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    sendPrompt();
  }, [
    organizationId,
    selectedDate,
    filters.course,
    filters.mode,
    filters.shift,
    filters.trainer,
    filters.spaceStatus,
    filters.status,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const updateMode = () => {
      const width = node.getBoundingClientRect().width;
      setIsCompactView(width < 1450);
    };

    updateMode();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => updateMode());
      observer.observe(node);
    } else {
      window.addEventListener("resize", updateMode);
    }

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", updateMode);
    };
  }, []);

  const spacesForTable = useMemo(() => {
    return spaces
      .filter((space) => {
        if (filters.spaceStatus === "All") return true;
        return String(space?.status || "").toLowerCase() === filters.spaceStatus.toLowerCase();
      })
      .map((space) => ({
        id: String(space.id),
        name: space.name || "-",
        floor_number:
          space?.floor_number === null || space?.floor_number === undefined
            ? "-"
            : String(space.floor_number),
        time_slots: Array.isArray(space?.time_slots) ? space.time_slots : [],
      }));
  }, [filters.spaceStatus, spaces]);

  const slotsForTable = useMemo(() => {
    const merged = [...DEFAULT_TIME_SLOTS];
    if (slots.length) {
      const fromApi = slots
        .map((slot) => {
          const key = normalizeSlotId(slot.id);
          return {
            key,
            label: formatHourLabel(slot),
          };
        })
        // Keep only hour anchors (e.g. 1000), not half-hour keys (e.g. 1030),
        // so the timeline stays aligned as one row per hour.
        .filter((slot) => slot.key.endsWith("00"));

      if (fromApi.length) {
        fromApi.forEach((slot) => {
          if (!merged.some((base) => base.key === slot.key)) {
            merged.push(slot);
          }
        });
        
      }
    }
    return merged.sort((a, b) => Number(a.key) - Number(b.key));
  }, [slots]);

  const bookingsBySpaceAndStartHour = useMemo(() => {
    const map = new Map();
    bookings.forEach((booking) => {
      const slotId = normalizeSlotId(booking.slotId || "");
      const startMinutes =
        timeToMinutes(booking.batchClassStartTime) ??
        timeToMinutes(booking._start) ??
        timeToMinutes(booking.start) ??
        timeToMinutes(slotId);
      const endMinutesRaw =
        timeToMinutes(booking.batchClassEndTime) ??
        timeToMinutes(booking._end) ??
        timeToMinutes(booking.end);
      const endMinutes =
        endMinutesRaw && startMinutes !== null && endMinutesRaw > startMinutes
          ? endMinutesRaw
          : startMinutes !== null
            ? startMinutes + 60
            : null;
      const spaceId = String(booking.spaceId || "");
      if (!slotId || !spaceId || startMinutes === null || endMinutes === null) return;
      const startHourKey = `${String(Math.floor(startMinutes / 60)).padStart(2, "0")}00`;
      const key = `${startHourKey}::${spaceId}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push({ ...booking, __startMinutes: startMinutes, __endMinutes: endMinutes });
    });
    map.forEach((arr) => arr.sort((a, b) => a.__startMinutes - b.__startMinutes));
    return map;
  }, [bookings]);

  const bookingsBySpace = useMemo(() => {
    const map = new Map();
    bookingsBySpaceAndStartHour.forEach((arr) => {
      arr.forEach((b) => {
        const spaceId = String(b.spaceId || "");
        if (!spaceId) return;
        if (!map.has(spaceId)) map.set(spaceId, []);
        map.get(spaceId).push(b);
      });
    });
    map.forEach((arr) => arr.sort((a, b) => a.__startMinutes - b.__startMinutes));
    return map;
  }, [bookingsBySpaceAndStartHour]);

  const spaceAvailabilityById = useMemo(() => {
    const map = new Map();
    spacesForTable.forEach((space) => {
      const intervals = (Array.isArray(space.time_slots) ? space.time_slots : [])
        .map((slot) => {
          const start = timeToMinutes(slot?.start_time);
          const end = timeToMinutes(slot?.end_time);
          if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
          return { start, end };
        })
        .filter(Boolean);
      map.set(space.id, intervals);
    });
    return map;
  }, [spacesForTable]);

  const rowStatusLookup = useMemo(() => {
    const map = new Map();
    rowStatus.forEach((row) => {
      if (row?.slotId) {
        map.set(normalizeSlotId(row.slotId), String(row.status || "free").toLowerCase());
      }
    });
    return map;
  }, [rowStatus]);

  const slotRows = useMemo(() => {
    return slotsForTable.map((slot) => {
      const rowHour = Number(slot.key.slice(0, 2));
      const rowStart = rowHour * 60;
      const rowMid = rowStart + 30;
      const rowEnd = rowStart + 60;

      const cellData = spacesForTable.map((space) => {
        const hits = bookingsBySpaceAndStartHour.get(`${slot.key}::${space.id}`) || [];
        const booking = hits[0] || null;

        const spaceBookings = bookingsBySpace.get(space.id) || [];
        const spaceAvailability = spaceAvailabilityById.get(space.id) || [];

        const topBooked = spaceBookings.some((b) =>
          hasOverlap(b.__startMinutes, b.__endMinutes, rowStart, rowMid)
        );
        const bottomBooked = spaceBookings.some((b) =>
          hasOverlap(b.__startMinutes, b.__endMinutes, rowMid, rowEnd)
        );
        const topAvailable = spaceAvailability.some((iv) =>
          hasOverlap(iv.start, iv.end, rowStart, rowMid)
        );
        const bottomAvailable = spaceAvailability.some((iv) =>
          hasOverlap(iv.start, iv.end, rowMid, rowEnd)
        );

        return {
          booking,
          fill: {
            top: topBooked ? "booked" : topAvailable ? "available" : "unavailable",
            bottom: bottomBooked ? "booked" : bottomAvailable ? "available" : "unavailable",
          },
        };
      });
      const bookedCount = cellData.filter((cell) => !!cell.booking).length;
      const status = rowStatusLookup.get(slot.key) || toStatus(bookedCount, Math.max(1, spacesForTable.length));
      return { slot, rowHour, cellData, status };
    }).filter((row) =>
      filters.status === "All" ? true : row.status === filters.status.toLowerCase()
    );
  }, [
    bookingsBySpaceAndStartHour,
    bookingsBySpace,
    filters.status,
    rowStatusLookup,
    selectedDate,
    spaceAvailabilityById,
    slotsForTable,
    spacesForTable,
  ]);

  const handleTimelineMouseMove = (event, rowHour, spaceId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const ratio = Math.max(0, Math.min(1, y / rect.height));
    // Snap to 5-minute points for stable readable values.
    const minuteOffset = Math.min(55, Math.max(0, Math.round((ratio * 60) / 5) * 5));
    const totalMinutes = rowHour * 60 + minuteOffset;
    setHoverTimeState({
      key: `${rowHour}-${spaceId}`,
      value: formatMinutesToTime(totalMinutes),
      top: `${Math.max(4, Math.min(rect.height - 22, y))}px`,
    });
  };

  const filterConfigWithApiOptions = useMemo(() => {
    return FILTER_CONFIG.map((item) => ({
      ...item,
      options:
        Array.isArray(filterOptions[item.key]) && filterOptions[item.key].length
          ? filterOptions[item.key]
          : item.options,
    }));
  }, [filterOptions]);

  return (
    <section
      ref={rootRef}
      className={`slot-availability ${isCompactView ? "slot-availability--compact" : "slot-availability--expanded"}`}
    >
      <header className="slot-availability__header">
        <h2 className="slot-availability__title">Slot availability</h2>
        <div className="slot-availability__legend">
          <span className="slot-availability__legend-item">
            <i className="slot-status-dot slot-status-dot--free" />
            Available
          </span>
          <span className="slot-availability__legend-item">
            <i className="slot-status-dot slot-status-dot--partial" />
            Not Available
          </span>
          <span className="slot-availability__legend-item">
            <i className="slot-status-dot slot-status-dot--occupied" />
            Class Running
          </span>
        </div>
      </header>

      <div className="slot-availability__date-row">
        <label className="date-chip">
          <span>Date</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </label>
      </div>

      <div className="slot-availability__filters">
        {filterConfigWithApiOptions.map((filter) => (
          <label className="slot-select" key={filter.key}>
            <span>{filter.label}</span>
            <select
              value={filters[filter.key]}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, [filter.key]: event.target.value }))
              }
            >
              {filter.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <div className="slot-availability__days-row">
        <button type="button" className="search-button" onClick={sendPrompt}>
          {loading ? "Loading..." : "Search availability"}
        </button>
      </div>

      <div className="slot-availability__table-wrap">
        <table className="slot-grid">
          <thead>
            <tr>
              <th>Time</th>
              {spacesForTable.map((space) => (
                <th key={space.id}>
                  <div className="slot-space-head">
                    <span className="slot-space-head__name">{space.name}</span>
                    <span className="slot-space-head__floor">Floor: {space.floor_number}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slotRows.map(({ slot, rowHour, cellData, status }) => (
              <tr key={slot.key}>
                <td className="slot-time">
                  <div className="slot-time-split">
                    <span>{getHalfHourLabels(slot.key).top}</span>
                    <span>{getHalfHourLabels(slot.key).bottom}</span>
                  </div>
                </td>
                {cellData.map((cell, idx) => (
                  <td
                    className={`timeline-cell timeline-cell--${cell.fill.top} timeline-cell-bottom--${cell.fill.bottom}`}
                    key={`${slot.key}-${spacesForTable[idx]?.id || idx}`}
                    onMouseMove={(event) =>
                      handleTimelineMouseMove(
                        event,
                        rowHour,
                        spacesForTable[idx]?.id || String(idx)
                      )
                    }
                    onMouseLeave={() => setHoverTimeState(null)}
                  >
                    {cell.booking ? (() => {
                      const batchCode = getBatchCodeLabel(cell.booking);
                      const startTime = formatTimeAmPm(
                        cell.booking.batchClassStartTime || cell.booking._start || cell.booking.start || slot.key
                      );
                      const endTime = formatTimeAmPm(
                        cell.booking.batchClassEndTime || cell.booking._end || cell.booking.end
                      );
                      const hoverTitle = [
                        `Batch: ${batchCode}`,
                        `Trainer: ${cell.booking.trainerName || "-"}`,
                        `Program: ${cell.booking.course || "-"}`,
                        `Start: ${startTime}`,
                        `End: ${endTime}`,
                      ].join("\n");
                      const rowStart = rowHour * 60;
                      const topPercent = Math.max(
                        0,
                        Math.min(100, ((cell.booking.__startMinutes - rowStart) / 60) * 100)
                      );
                      const heightPercent = Math.max(
                        10,
                        ((cell.booking.__endMinutes - cell.booking.__startMinutes) / 60) * 100
                      );
                      return (
                        <div
                          className="booked-card timeline-booked-card"
                          style={{ top: `${topPercent}%`, height: `${heightPercent}%` }}
                          title={hoverTitle}
                        >
                          <strong>{batchCode}</strong>
                        </div>
                      );
                    })() : (
                      <div className="slot-empty slot-empty--timeline" />
                    )}
                    {hoverTimeState?.key === `${rowHour}-${spacesForTable[idx]?.id || String(idx)}` ? (
                      <span className="timeline-hover-time" style={{ top: hoverTimeState.top }}>
                        {hoverTimeState.value}
                      </span>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
            {!slotRows.length ? (
              <tr>
                <td colSpan={Math.max(2, spacesForTable.length + 1)} className="slot-time">
                  No availability data for selected filters/date.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SlotAvailability;
